import { Alert } from '@/components/ui/alert';
import { AlertDescription, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { BulkEmailPartition } from './bulk-email-recipients';

interface BulkEmailNoteProps {
  partition: BulkEmailPartition;
  /** Show the "X of N have an email" counter above the warning (channel step). */
  showCounter?: boolean;
  /**
   * Show the "N without email will not receive" warning. Only true when the
   * EMAIL channel is at stake: in link mode every creator gets an invitation
   * (mail-less ones are anchored on their social profile), so the warning
   * would be false there.
   */
  showWarning?: boolean;
}

/**
 * The email-coverage note for a bulk selection: an optional "X of N have an
 * email" counter and, for the email channel, the shared orange warning alert
 * ("N without email will not receive"). Shared by the channel step (counter +
 * mode-dependent warning) and the email delivery step (warning under the
 * banner).
 */
export const BulkEmailNote = ({
  partition,
  showCounter,
  showWarning = true,
}: BulkEmailNoteProps) => {
  const { t } = useTranslation();
  const { emailCount, totalCount, withoutEmail } = partition;
  const missingCount = withoutEmail.length;
  const warningVisible = showWarning && missingCount > 0;

  if (!showCounter && !warningVisible) return null;

  return (
    <Flex direction="column" gap="1.5">
      {showCounter && (
        <Text fontSize="12px" color="fg.muted">
          {t('invitations.modal.channel.bulk.emailCount', { emailCount, totalCount })}
        </Text>
      )}
      {warningVisible && (
        <Alert status="warning" colorPalette="orange" p={3} rounded="lg">
          <AlertDescription>
            <Text fontSize="12px" lineHeight="1.4">
              {t('invitations.modal.channel.bulk.noEmailNote', { count: missingCount })}
            </Text>
          </AlertDescription>
        </Alert>
      )}
    </Flex>
  );
};
